import { useEffect, useRef, useState } from 'react';

import { Spinner } from './Animations';
import { Button } from './Button';
import { cn } from './cn';
import { useInfiniteScroll } from './useInfiniteScroll';

/** The one wording every list uses, so the end of a list reads the same on every surface (#8630). */
export const INFINITE_SCROLL_LOAD_MORE_LABEL = 'Load more';
export const INFINITE_SCROLL_LOADING_LABEL = 'Loading more…';
/** The load-all mode's line, shown until the last page is in. */
export const INFINITE_SCROLL_LOADING_ALL_LABEL = 'Loading all items…';
export const INFINITE_SCROLL_RETRY_LABEL = 'Retry';
export const INFINITE_SCROLL_ERROR_MESSAGE = "Couldn't load more.";
export const INFINITE_SCROLL_END_LABEL = 'End of list';
/** The end line when the caller knows how many rows it holds. */
export const infiniteScrollEndLabel = (itemCount?: number) =>
  itemCount === undefined ? INFINITE_SCROLL_END_LABEL : `All ${itemCount} items loaded`;

export type InfiniteScrollFooterProps = {
  /**
   * `true`: the footer shows and the next page loads as it scrolls into view. `false`: the list is complete; the
   * footer then says so if it loaded at least one more page, and renders nothing otherwise. `undefined`: the footer
   * shows with the button only, because the caller has not said whether there is more, so nothing loads on its own.
   */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** The last next-page fetch failed: the footer says so and offers a retry instead of loading again by itself. */
  isError?: boolean;
  onLoadMore: () => void;
  /**
   * The one exception to loading on scroll: a service that cannot serve a list in parts. The footer then requests
   * every remaining page itself, says "Loading all items…" until the last one is in, and ends like any other list.
   * A failed page still stops at Retry. Use it only where paging is impossible, never where it is inconvenient.
   */
  loadAll?: boolean;
  /** How many rows the list holds now, for the end line ("All 143 items loaded"). Left out, it says "End of list". */
  itemCount?: number;
  /** Re-arms the sentinel when it changes; defaults to `itemCount`. See `useInfiniteScroll`. */
  resetKey?: unknown;
  className?: string;
};

/**
 * The end of a paginated list: a sentinel that requests the next page as it scrolls into view, and a status line
 * that tells a screen reader when more is loading. At rest it is nothing but the sentinel: no control to press,
 * because reaching the end of the list is the request, by pointer or by keyboard alike.
 *
 * A button appears only where the list cannot load on its own: after a failed page (Retry), and when the caller
 * has not said whether there is more (Load more). Once shown, it stays mounted and focusable while the page it
 * asked for loads, so focus is not thrown back to the top of the list when the rows re-render.
 *
 * Once the last page is in, a list that paged says so in one quiet line, so a silent stop is never mistaken for a
 * sentinel that did not fire. A list that fit in one page shows nothing, exactly as it did before it could page.
 *
 * With `loadAll`, the footer loads every page on its own instead of waiting for the sentinel: the documented
 * exception for a service that cannot page, in the same component rather than a second pattern.
 */
export const InfiniteScrollFooter = ({
  hasNextPage,
  isFetchingNextPage,
  isError,
  onLoadMore,
  loadAll = false,
  itemCount,
  resetKey = itemCount,
  className,
}: InfiniteScrollFooterProps) => {
  // In load-all mode the sentinel stays inert; the effect below does the asking.
  const sentinelRef = useInfiniteScroll({
    hasNextPage: loadAll ? false : hasNextPage,
    isFetchingNextPage,
    isError,
    onLoadMore,
    resetKey,
  });

  // Load-all: request the next page whenever one is there and nothing is in flight. A failed page stops here
  // too, or the effect would retry the failing endpoint on every settle; Retry resumes it.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  useEffect(() => {
    if (!loadAll || hasNextPage !== true || isFetchingNextPage || isError) return;
    onLoadMoreRef.current();
  }, [loadAll, hasNextPage, isFetchingNextPage, isError]);

  // Whether this footer loaded a page beyond the first. Only then is the end worth announcing.
  const [hasPaged, setHasPaged] = useState(false);
  useEffect(() => {
    if (isFetchingNextPage) setHasPaged(true);
  }, [isFetchingNextPage]);

  // Whether the button was on screen when the current fetch started. A fetch the button asked for keeps it
  // mounted until it settles, even if the flags that showed it clear meanwhile, so focus has somewhere to stay.
  const buttonAtRest = isError || hasNextPage === undefined;
  const buttonAtRestRef = useRef(buttonAtRest);
  if (!isFetchingNextPage) buttonAtRestRef.current = buttonAtRest;
  const showButton = buttonAtRest || (isFetchingNextPage && buttonAtRestRef.current);

  if (hasNextPage === false) {
    if (!hasPaged) return null;
    return (
      <div className={cn('flex justify-center border-t border-border py-3', className)}>
        <p role="status" aria-live="polite" className="text-sm text-text-muted" data-test="infinite-scroll-end">
          {infiniteScrollEndLabel(itemCount)}
        </p>
      </div>
    );
  }

  const loadingLabel = loadAll ? INFINITE_SCROLL_LOADING_ALL_LABEL : INFINITE_SCROLL_LOADING_LABEL;
  const status = isFetchingNextPage ? loadingLabel : isError ? INFINITE_SCROLL_ERROR_MESSAGE : '';
  const hasVisibleContent = showButton || isFetchingNextPage;

  return (
    <div
      className={cn('flex flex-col items-center gap-2 border-t border-border', hasVisibleContent && 'py-3', className)}
    >
      <div ref={sentinelRef} className="h-px w-full" aria-hidden data-test="infinite-scroll-sentinel" />
      {/* Always in the tree, so the live region exists before it has something to announce. */}
      <p
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 text-sm text-text-muted empty:sr-only"
        data-test="infinite-scroll-status"
      >
        {isFetchingNextPage && (
          <Spinner aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" transition={{ duration: 1 }}>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </Spinner>
        )}
        {status}
      </p>
      {showButton && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onLoadMore}
          loading={isFetchingNextPage}
          focusableWhenDisabled
          data-test="infinite-scroll-load-more"
        >
          {isError ? INFINITE_SCROLL_RETRY_LABEL : INFINITE_SCROLL_LOAD_MORE_LABEL}
        </Button>
      )}
    </div>
  );
};
